import { createSelector } from "reselect";

import {
  SaleyardPermissions,
  DeploymentPermissions,
} from "constants/permissions";
import { SaleType } from "constants/sale";

import { EMPTY_ARRAY, EMPTY_OBJECT } from "lib";

import { getLivestockSaleId, getSaleyardName } from "lib/navigation";
import { hasPermission } from "lib/permissions";

import { getActiveLivestockAgentDeployment } from "./deploymentActiveAgent";
import { createIdByKeySelectorAndGetter } from "./lib";
import { getSales, getSaleyards } from "./root";
import { selectCurrentSaleyard } from "./saleyards";

export const getCurrentSale = state =>
  getSales(state)[getLivestockSaleId()] || null;

// This can be used in locations where we don't have a saleid in our url, but still need a sale.
export const getCurrentOrFirstSale = state =>
  getLivestockSaleId()
    ? getSales(state)[getLivestockSaleId()]
    : Object.values(getSales(state))[0] || null;

export const getCurrentDeploymentSalesList = state =>
  getCurrentSale(state)?.deployment_sales || null;

export const getCurrentRoundsList = state =>
  getCurrentSale(state).rounds || null;

export const getCurrentSpeciesId = state =>
  getCurrentSale(state)?.species_id || null;

export const getCurrentSaleType = state =>
  getCurrentSale(state)?.sale_type || null;

export const getIsCurrentSaleSaleType = saleType => state =>
  getCurrentSaleType(state) === saleType;

export const getIsCurrentSaleClearingSale = state =>
  getIsCurrentSaleSaleType(SaleType.CLEARING)(state);

export const selectCurrentDeploymentSales = createSelector(
  getCurrentSale,
  currentSale =>
    currentSale?.deployment_sales.reduce((acc, deploymentSale) => {
      acc[deploymentSale.deployment_sale_id] = deploymentSale;
      return acc;
    }, {}) || EMPTY_OBJECT,
);

export const getDeploymentSaleById = deploymentSaleId => state =>
  selectCurrentDeploymentSales(state)[deploymentSaleId] || null;

export const selectCurrentDeploymentSaleIdsList = createSelector(
  getCurrentSale,
  currentSale =>
    currentSale?.deployment_sales?.map(
      deploymentSale => deploymentSale.deployment_sale_id,
    ) || [],
);

export const getCurrentDeploymentSaleIds = state =>
  selectCurrentDeploymentSaleIdsList(state);

export const getHasSaleWatcherInCurrentSale = createSelector(
  getCurrentSale,
  currentSale => !!currentSale?.watch_link,
);

export const [selectIdBySaleyardNameLookup, getIdBySaleyardName] =
  createIdByKeySelectorAndGetter(getSaleyards, "name");

export const getCurrentSaleyardId = state =>
  getCurrentSale(state)?.saleyard_id ||
  getIdBySaleyardName(getSaleyardName())(state) ||
  null;

export const getCurrentSaleyard = state =>
  getSaleyards(state)[getCurrentSaleyardId(state)];
export const selectCurrentDeploymentIds = createSelector(
  [getCurrentDeploymentSalesList],
  deploymentsList =>
    deploymentsList?.map(deployment => deployment.deployment_id) || EMPTY_ARRAY,
);
export const selectShowCheckPoints = createSelector(
  [getActiveLivestockAgentDeployment, selectCurrentSaleyard],
  (activeLivestockAgentDeployment, currentSaleyard) =>
    hasPermission(currentSaleyard, SaleyardPermissions.canCreateCheckpoint) ||
    hasPermission(
      activeLivestockAgentDeployment,
      DeploymentPermissions.canCreateCheckpoint,
    ),
);
